import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Preloader, Responsive, Text, useResponsive } from '@common-fe/common-fe';

import {
  CLAIM_AGREEMENT_ERROR_TEXT,
  CLAIM_INVALID_PAYMENT_METHOD_TEXT,
  CLAIM_OCR_ERROR_TEXT,
} from '@/common/constants';
import AppButton from '@/components/controls/AppButton';

import { ReimburseFooterBackground } from '../ReimburseFooterBackground';

import CancelClaimButton from './CancelClaimButton';

const RETRY_ERROR = 'The server encountered an issue processing your request. Please try again in 2 minutes.';

interface Props {
  notification?: React.ReactNode;
  isLoading?: boolean;
  onBack?: () => void;
  onSave: (isSuccessful: boolean) => void;
  onCancel: () => void;
  isAccepted: boolean;
  isRetryError: boolean;
  submitButtonTitle?: string;
  isBillPayMode?: boolean;
  hasAdditionalError: boolean;
  isOcrErrors?: boolean;
  isPaymentMethodError?: boolean;
}
const AcceptFooter: React.FC<Props> = ({
  notification,
  isLoading,
  onBack,
  onSave,
  onCancel,
  isAccepted,
  isOcrErrors,
  submitButtonTitle,
  isBillPayMode,
  hasAdditionalError,
  isRetryError,
  isPaymentMethodError,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleClick = () => {
    if (onBack) {
      onBack();
    }
  };

  const onSubmit = useCallback(() => {
    setIsSubmitted(true);

    if (isPaymentMethodError) {
      setError(CLAIM_INVALID_PAYMENT_METHOD_TEXT);
      onSave(false);
    }

    if (isOcrErrors) {
      setError(CLAIM_OCR_ERROR_TEXT);
      onSave(false);
    }

    if (isAccepted && !hasAdditionalError) {
      onSave(true);
    }
    if (!isAccepted) {
      setError(CLAIM_AGREEMENT_ERROR_TEXT);
      onSave(false);
    }
    if (hasAdditionalError) {
      onSave(false);
    }
  }, [isOcrErrors, isAccepted, hasAdditionalError, onSave, isPaymentMethodError]);

  useEffect(() => {
    if (isAccepted && error && !isOcrErrors && !isPaymentMethodError) {
      setError('');
    }
  }, [isAccepted, error, isOcrErrors, isPaymentMethodError]);

  const currentHeight = useMemo(() => {
    // TODO: when will rework reimburse me remove  <ReimburseFooterBackground and refactor it without position absolute with fixed height
    return error || isRetryError || ((isSubmitted || hasAdditionalError) && notification && !error)
      ? isMobile
        ? '156px'
        : '127px'
      : '88px';
  }, [error, hasAdditionalError, isMobile, isRetryError, isSubmitted, notification]);
  return (
    <>
      <Box
        wrap
        direction="column"
        justify="end"
        height={{ min: currentHeight }}
        background="module"
        pad={{ bottom: 'spacing24' }}
        style={{
          zIndex: 1,
        }}
      >
        {error && (
          <Box align="end" width="100%" margin={{ bottom: 'spacing12' }}>
            <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
              {t(error)}
            </Text>
          </Box>
        )}
        {isRetryError && (
          <Box align="end" width="100%" margin={{ bottom: 'spacing12' }}>
            <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
              {t(RETRY_ERROR)}
            </Text>
          </Box>
        )}
        {(isSubmitted || hasAdditionalError) && !error && notification}
        <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
          <Box direction="row" gap="12px" justify="between" align="center" width="100%">
            <CancelClaimButton onCancel={onCancel} isBillPayMode={isBillPayMode} />

            <Box direction="row" gap="12px">
              <AppButton
                testId="cancel"
                buttonType="secondary"
                onClick={handleClick}
                disabled={isLoading}
                width="control"
              >
                {isLoading ? <Preloader /> : t('Back')}
              </AppButton>

              <AppButton
                testId="submit"
                width={!submitButtonTitle || isLoading ? 'control' : 'max-content'}
                onClick={onSubmit}
                disabled={isLoading}
              >
                {isLoading && <Preloader color="white" />}
                {!isLoading && (submitButtonTitle || t('Submit'))}
              </AppButton>
            </Box>
          </Box>
        </Responsive>
        <Responsive size={Responsive.Mobile}>
          <Box direction="row" gap="12px">
            <Box fill>
              <AppButton
                testId="back"
                fill={isMobile}
                style={{ flex: 1 }}
                buttonType="secondary"
                onClick={handleClick}
                disabled={isLoading}
              >
                {isLoading ? <Preloader /> : t('Back')}
              </AppButton>
            </Box>
            <Box fill>
              <AppButton
                testId="submit"
                fill={isMobile}
                style={{ flex: 1 }}
                onClick={onSubmit}
                disabled={isLoading}
              >
                {isLoading && <Preloader color="white" />}
                {!isLoading && (submitButtonTitle || t('Submit'))}
              </AppButton>
            </Box>
          </Box>
        </Responsive>
      </Box>
      <ReimburseFooterBackground height={currentHeight} />
    </>
  );
};

export default AcceptFooter;
